import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/Fulfillment.css';

function ReturnDetails({ email, onLogout }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [canProcess, setCanProcess] = useState(false);
  const [rejectionDetails, setRejectionDetails] = useState({
    images: [],
    description: '',
    reason: ''
  });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [existingReturn, setExistingReturn] = useState(null);
  const [generatingPickup, setGeneratingPickup] = useState(false);

  const returnReasons = [
    'Damaged Item',
  ];

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem('authKey');
      const userId = localStorage.getItem('userId');

      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }

      const response = await fetch(`${API_BASE_URL}/brands/orders/${orderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch return details');
      }

      const data = await response.json();
      const orderData = data.data;
      
      if (!orderData) {
        throw new Error('No order data found in response');
      }

      setOrder(orderData);
    } catch (err) {
      console.error('Error in fetchOrderDetails:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const checkReturnStatus = useCallback(async () => {
    try {
      const authKey = localStorage.getItem('authKey');
      const userId = localStorage.getItem('userId');

      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }

      const response = await fetch(`${API_BASE_URL}/brands/orders/${orderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to check return status');
      }

      const data = await response.json();
      console.log('Return status API response:', data);
      console.log('Can process value:', data.data.can_process);
      setCanProcess(data.data.can_process);
      setExistingReturn(data.data.existing_return || null);
      
    } catch (err) {
      console.error('Error checking return status:', err);
      setError(err.message);
    }
  }, [orderId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOrderDetails(),
        checkReturnStatus()
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchOrderDetails, checkReturnStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Add effect to log state changes
  useEffect(() => {
    // console.log('Current order state:', order);
  }, [order]);

  useEffect(() => {
    // console.log('Current error state:', error);
  }, [error]);

  useEffect(() => {
    // console.log('Current loading state:', loading);
  }, [loading]);

  // Add effect to monitor canProcess changes
  useEffect(() => {
    console.log('canProcess state changed:', canProcess);
  }, [canProcess]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length + rejectionDetails.images.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setRejectionDetails(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
    if (files.length + rejectionDetails.images.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setRejectionDetails(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const handleRemoveImage = (index) => {
    setRejectionDetails(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitRejection = async () => {
    try {
      setSubmitting(true);
      setSubmitError('');
      
      const authKey = localStorage.getItem('authKey');
      const userId = localStorage.getItem('userId');

      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }
      
      // Create FormData instance
      const formData = new FormData();
      
      // Add text fields
      formData.append('order_id', orderId);
      formData.append('reason', rejectionDetails.reason);
      formData.append('description', rejectionDetails.description);
      formData.append('auth_key', authKey);
      formData.append('user_id', userId);
      
      // Add images
      rejectionDetails.images.forEach((image, index) => {
        formData.append(`image_${index}`, image.file);
      });
      
      const response = await fetch(`${API_BASE_URL}/brands/returns/reject/${orderId}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit rejection');
      }
      
      // Clear form and close it
      setRejectionDetails({
        images: [],
        description: '',
        reason: ''
      });
      setShowRejectForm(false);
      
      // Refresh order details to show updated status
      fetchOrderDetails();
      
    } catch (err) {
      console.error('Error submitting rejection:', err);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Clean up image URLs when component unmounts or images are removed
  useEffect(() => {
    // Revoke object URLs when component unmounts
    return () => {
      rejectionDetails.images.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [rejectionDetails.images]);

  const handleGeneratePickup = async (decision, reason = '') => {
    try {
      setGeneratingPickup(true);
      setSubmitError('');

      const authKey = localStorage.getItem('authKey');
      const userId = localStorage.getItem('userId');

      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }

      // Get the first item with return details
      const product_id = order.items.find(item => item.product_id);
      if (!product_id) {
        throw new Error('No return item found');
      }

      const payload = {
        auth_key: authKey,
        user_id: userId,
        order_id: orderId,
        item_id: product_id.product_id,
        decision: decision
      };

      if (reason) {
        payload.reason = reason;
      }
      console.log('Payload:', payload);
      const response = await fetch(`${API_BASE_URL}/brands/returns/pickup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate pickup request');
      }

      // Refresh order details to show updated status
      await Promise.all([
        fetchOrderDetails(),
        checkReturnStatus()
      ]);

    } catch (err) {
      console.error('Error generating pickup:', err);
      setSubmitError(err.message);
    } finally {
      setGeneratingPickup(false);
    }
  };

  // First find the return item
  const returnItem = order?.items?.find(item => item.return_details);
  const returnStatus = returnItem?.return_details?.status;
  const showActionButtons = returnStatus === 'pending';

  if (loading) {
    // console.log('Rendering loading state');
    return (
      <div className="fulfillment-loading">
        <div className="loader"></div>
        <p>Loading return details...</p>
      </div>
    );
  }

  if (error) {
    // console.log('Rendering error state:', error);
    return (
      <div className="fulfillment-error">
        <p>{error}</p>
        <button onClick={() => navigate('/returns')}>Back to Returns</button>
      </div>
    );
  }

  if (!order) {
    // console.log('Rendering empty order state');
    return (
      <div className="fulfillment-error">
        <p>Return not found</p>
        <button onClick={() => navigate('/returns')}>Back to Returns</button>
      </div>
    );
  }

//   console.log('Rendering order details:', order);
  return (
    <div className="dashboard-root">
      <TopMenu pageTitle={`Return Details`} email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="fulfillment">
          <div className="order-details-grid">
            <div className="detail-section">
              <h4>Customer Information</h4>
              {order.shipping_address ? (
                <>
                  <p>Name: {order.shipping_address.name || 'N/A'}</p>
                  <p>Phone: {order.shipping_address.phone || 'N/A'}</p>
                </>
              ) : (
                <p>No customer information available</p>
              )}
            </div>

            {order.shipping_address && (
              <div className="detail-section">
                <h4>Shipping Address</h4>
                <p>{order.shipping_address.name}</p>
                <p>{order.shipping_address.address1}</p>
                {order.shipping_address.address2 && <p>{order.shipping_address.address2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.state}</p>
                <p>{order.shipping_address.pincode}</p>
              </div>
            )}

            <div className="detail-section">
              <h4>Order Items</h4>
              <div className="order-items">
                {order.items && order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <p className="item-title">{item.name}</p>
                    <p className="item-variant">Variant: {item.variant_title || 'Default'}</p>
                    <p className="item-quantity">Quantity: {item.quantity}</p>
                    <p className="item-price">Price: {order.payment_details.currency} {item.price}</p>
                    {item.shipping_info && (
                      <p className="item-status">Shipping Status: {item.shipping_info.current_status || item.shipping_info.status}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-section">
              <h4>Order Summary</h4>
              <p>Total Items: {order.items ? order.items.length : 0}</p>
              <p>Order Total: {order.payment_details.currency} {order.payment_details.amount}</p>
              <p>Order Status: {order.status}</p>
              <p>Created At: {formatDate(order.created_at)}</p>
              <p>Updated At: {formatDate(order.updated_at)}</p>
            </div>

            {showActionButtons ? (
              <>
                <div className="detail-section action-section">
                  <h4>Generate Pickup Request</h4>
                  {submitError && (
                    <div className="error-message">
                      {submitError}
                    </div>
                  )}
                  <div className="action-buttons">
                    <button 
                      className="approve-button"
                      onClick={() => handleGeneratePickup('accepted')}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <div className="button-loader"></div>
                          Approving...
                        </>
                      ) : (
                        'Approve'
                      )}
                    </button>
                    <button 
                      className="reject-button"
                      onClick={() => setShowRejectForm(!showRejectForm)}
                      disabled={submitting}
                    >
                      Reject
                    </button>
                  </div>
                </div>

                {showRejectForm && (
                  <div className="detail-section reject-form-section">
                    <h4>Rejection Details</h4>
                    <div className="reject-form">
                      <div className="form-group">
                        <label>Reason for Rejection:</label>
                        <select 
                          value={rejectionDetails.reason}
                          onChange={(e) => setRejectionDetails(prev => ({
                            ...prev,
                            reason: e.target.value
                          }))}
                          disabled={submitting}
                        >
                          <option value="">Select a reason</option>
                          {returnReasons.map(reason => (
                            <option key={reason} value={reason}>{reason}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label>Description:</label>
                        <textarea
                          value={rejectionDetails.description}
                          onChange={(e) => setRejectionDetails(prev => ({
                            ...prev,
                            description: e.target.value
                          }))}
                          placeholder="Enter detailed reason for rejection"
                          disabled={submitting}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Upload Images (max 5):</label>
                        <div 
                          className={`drop-zone ${isDragging ? 'dragging' : ''} ${submitting ? 'disabled' : ''}`}
                          onDragEnter={!submitting ? handleDragEnter : undefined}
                          onDragLeave={!submitting ? handleDragLeave : undefined}
                          onDragOver={!submitting ? handleDragOver : undefined}
                          onDrop={!submitting ? handleDrop : undefined}
                          onClick={() => !submitting && fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                            disabled={submitting || rejectionDetails.images.length >= 5}
                          />
                          <div className="drop-zone-content">
                            <i className="fas fa-cloud-upload-alt"></i>
                            <p>Drag & drop images here or click to browse</p>
                            <span>Maximum 5 images allowed</span>
                          </div>
                        </div>
                        
                        {rejectionDetails.images.length > 0 && (
                          <div className="image-preview">
                            {rejectionDetails.images.map((image, index) => (
                              <div key={index} className="image-preview-item">
                                <img src={image.preview} alt={`${index + 1}`} />
                                <div className="image-preview-overlay">
                                  <button 
                                    onClick={() => !submitting && handleRemoveImage(index)}
                                    disabled={submitting}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <button 
                        className="submit-button"
                        onClick={handleSubmitRejection}
                        disabled={submitting || !rejectionDetails.reason || !rejectionDetails.description}
                      >
                        {submitting ? (
                          <>
                            <div className="button-loader"></div>
                            Submitting...
                          </>
                        ) : (
                          'Submit Rejection'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="detail-section action-section">
                <h4>Return Action Status</h4>
                <div className="action-status">
                  <div className={`status-badge ${returnStatus}`}>
                    {returnStatus?.toUpperCase()}
                  </div>
                  <div className="action-details">
                    <p>
                      <strong>Action Date:</strong> {formatDate(returnItem?.return_details?.created_at)}
                    </p>
                    <p>
                      <strong>Last Updated:</strong> {formatDate(returnItem?.return_details?.updated_at)}
                    </p>
                    <p>
                      <strong>Processed By:</strong> {existingReturn?.email}
                    </p>
                    {returnStatus === 'approved' && (
                      <div className="pickup-actions">
                        <h4>Generate Pickup Request</h4>
                        <div className="action-buttons">
                          <button 
                            className="approve-button"
                            onClick={() => handleGeneratePickup('accepted')}
                            disabled={generatingPickup}
                          >
                            {generatingPickup ? (
                              <>
                                <div className="button-loader"></div>
                                Generating Pickup...
                              </>
                            ) : (
                              'Generate Pickup'
                            )}
                          </button>
                          <button 
                            className="reject-button"
                            onClick={() => handleGeneratePickup('rejected', 'Product not eligible for return')}
                            disabled={generatingPickup}
                          >
                            Reject Pickup
                          </button>
                        </div>
                      </div>
                    )}
                    {returnStatus === 'rejected' && (
                      <>
                        <div className="rejection-details">
                          <p>
                            <strong>Reason:</strong> {returnItem?.return_details?.reason}
                          </p>
                          <p>
                            <strong>Description:</strong> {returnItem?.return_details?.description}
                          </p>
                          {returnItem?.return_details?.images && returnItem?.return_details?.images.length > 0 && (
                            <div className="rejection-images">
                              <p><strong>Rejection Images:</strong></p>
                              <div className="image-preview">
                                {returnItem?.return_details?.images.map((image, index) => (
                                  <div key={index} className="image-preview-item">
                                    <img src={image.url} alt={`Return ${index + 1}`} />
                                    <div className="image-preview-overlay">
                                      <a 
                                        href={image.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="view-image-button"
                                      >
                                        <i className="fas fa-external-link-alt"></i>
                                      </a>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReturnDetails; 